/**
 * Service charges — one-time fees for work delivered by hand.
 *
 * Staff create a charge; the customer confirms it in the portal, which is what
 * places the hold. Staff never authorize on a customer's behalf: the customer's
 * confirmation is both the authorization record and what makes the transaction
 * customer-initiated, which earns the longer 7-day hold window.
 *
 * Capture collects the money and should happen only once the work is actually
 * delivered.
 */

import { apiGet, apiPost } from './client';

export type ServiceChargeStatus =
  | 'draft'
  | 'authorized'
  | 'delivered'
  | 'captured'
  | 'expired'
  | 'voided'
  | 'failed'
  | 'refunded';

export interface ServiceCharge {
  id: string;
  customer_id: string;
  website_id: string | null;
  service_type: string;
  description: string;
  amount_cents: number;
  currency: string;
  charge_mode: 'immediate' | 'authorize-capture';
  status: ServiceChargeStatus;
  stripe_payment_intent_id: string | null;
  authorized_at: string | null;
  authorization_expires_at: string | null;
  delivered_at: string | null;
  delivered_by: string | null;
  captured_at: string | null;
  captured_amount_cents: number | null;
  failure_reason: string | null;
  decline_code: string | null;
  created_at: string;
}

export async function listServiceCharges(status?: ServiceChargeStatus): Promise<ServiceCharge[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await apiGet<{ data: ServiceCharge[] }>(`/api/admin/service-charges${query}`);
  return response.data;
}

export interface CreateServiceChargeInput {
  customer_id: string;
  website_id?: string;
  service_type: string;
  description: string;
  amount_cents: number;
}

export interface CreateServiceChargeResult {
  data: ServiceCharge;
  /** Whether the customer was told it is waiting for them. */
  notified: boolean;
  /** Why notification did not happen, when it did not. Follow up by hand. */
  notify_error: string | null;
  /** Set when the customer has no card on file and so cannot confirm. */
  warning: string | null;
}

export async function createServiceCharge(
  input: CreateServiceChargeInput
): Promise<CreateServiceChargeResult> {
  return apiPost<CreateServiceChargeResult>('/api/admin/service-charges', input);
}

/** Preset fees from the add-on ladder, so amounts stay consistent between staff. */
export const SERVICE_PRESETS = [
  { type: 'email-send-as-setup', label: 'Branded email setup', amountCents: 9900 },
  { type: 'byop-handoff', label: 'Microsoft 365 / Google Workspace setup', amountCents: 14900 },
  { type: 'gbp-claim', label: 'Google Business Profile claim & verify', amountCents: 14900 },
  { type: 'email-migration', label: 'Email migration', amountCents: 24900 },
] as const;

/**
 * Collect the money. `amount_cents` captures LESS than authorized for a job
 * that scoped down — the remainder is released permanently, since Stripe
 * allows no second capture on the same authorization.
 */
export async function captureServiceCharge(
  chargeId: string,
  deliveredBy: string,
  amountCents?: number
): Promise<ServiceCharge> {
  const response = await apiPost<{ data: ServiceCharge }>(
    `/api/admin/service-charges/${chargeId}/capture`,
    { delivered_by: deliveredBy, amount_cents: amountCents }
  );
  return response.data;
}

/** Release the hold without collecting. The customer is never charged. */
export async function voidServiceCharge(
  chargeId: string,
  reason: string
): Promise<ServiceCharge> {
  const response = await apiPost<{ data: ServiceCharge }>(
    `/api/admin/service-charges/${chargeId}/void`,
    { reason }
  );
  return response.data;
}

export function formatAmount(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * Hours until a hold lapses. Negative means it already has.
 *
 * The window is typically 7 days but can be shorter, so this reads the real
 * deadline the server stored from Stripe rather than assuming one.
 */
export function hoursUntilExpiry(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt) - Date.now();
  if (Number.isNaN(ms)) return null;
  return ms / 3_600_000;
}
